// Minimal promise wrapper over IndexedDB: one database, one object store per kind of record, keys are strings.
// Everything the app keeps on the device that is not a cached HTTP response lives here (decisions.md, "On-device
// storage"). Tests use fake-indexeddb (import 'fake-indexeddb/auto').

export const DB_NAME = 'trailbivvy';
export const DB_VERSION = 1;

/** Object stores. Add a store by appending it here and bumping DB_VERSION. */
export const STORES = [
  'prefs', // typed preferences (src/storage/prefs.ts)
  'entitlement', // signed TrailBivvy Pro token (src/store)
  'packs', // decoded-pack bookkeeping: which packs are pinned offline
  'imports', // user imports (TrailDataset JSON)
  'trips', // recorded trips (UserTrip)
  'waypoints', // personal waypoints
  'photos', // waypoint photos (Blob)
  'reports', // user reports
  'weather', // weather cache (WeatherForecast per cell)
  'weatherResponses', // MET response store (Expires / Last-Modified)
  'offlineRegions', // OfflineRegion records + plans
] as const;

export type StoreName = (typeof STORES)[number];

let dbPromise: Promise<IDBDatabase> | undefined;

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function openDatabase(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        for (const name of STORES) if (!db.objectStoreNames.contains(name)) db.createObjectStore(name);
      };
      req.onsuccess = () => {
        const db = req.result;
        // Another tab upgraded the schema: close so it can proceed; the next call reopens.
        db.onversionchange = () => {
          db.close();
          dbPromise = undefined;
        };
        resolve(db);
      };
      req.onerror = () => {
        dbPromise = undefined;
        reject(req.error);
      };
      req.onblocked = () => reject(new Error('IndexedDB upgrade blocked by another tab'));
    });
  }
  return dbPromise;
}

/** Test hook: forget the cached connection (after deleting the database). */
export function resetDatabaseConnection(): void {
  dbPromise = undefined;
}

async function tx<T>(store: StoreName, mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDatabase();
  const t = db.transaction(store, mode);
  const result = request(run(t.objectStore(store)));
  await new Promise<void>((resolve, reject) => {
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error ?? new Error('transaction aborted'));
  });
  return result;
}

/** Key-value access to one object store. Values are structured-cloned (plain objects, arrays, Blobs). */
export interface KeyValueStore<T> {
  get(key: string): Promise<T | undefined>;
  set(key: string, value: T): Promise<void>;
  delete(key: string): Promise<void>;
  keys(): Promise<string[]>;
  values(): Promise<T[]>;
  entries(): Promise<Array<[string, T]>>;
  clear(): Promise<void>;
}

export function kvStore<T>(store: StoreName): KeyValueStore<T> {
  return {
    get: (key) => tx(store, 'readonly', (s) => s.get(key) as IDBRequest<T | undefined>),
    set: async (key, value) => {
      await tx(store, 'readwrite', (s) => s.put(value, key));
    },
    delete: async (key) => {
      await tx(store, 'readwrite', (s) => s.delete(key));
    },
    keys: async () => (await tx(store, 'readonly', (s) => s.getAllKeys())).map(String),
    values: () => tx(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>),
    entries: async () => {
      const [keys, values] = await Promise.all([
        tx(store, 'readonly', (s) => s.getAllKeys()),
        tx(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>),
      ]);
      return keys.map((k, i) => [String(k), values[i]] as [string, T]);
    },
    clear: async () => {
      await tx(store, 'readwrite', (s) => s.clear());
    },
  };
}

/** In-memory implementation with the same interface, for tests and for private-mode fallbacks. */
export function memoryStore<T>(): KeyValueStore<T> {
  const map = new Map<string, T>();
  return {
    get: async (key) => map.get(key),
    set: async (key, value) => {
      map.set(key, value);
    },
    delete: async (key) => {
      map.delete(key);
    },
    keys: async () => [...map.keys()],
    values: async () => [...map.values()],
    entries: async () => [...map.entries()],
    clear: async () => map.clear(),
  };
}
